import { Substitute } from "@finway-group/shared/lib/models/user/Substitute"
import { Form, Modal } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { SubstituteForm, SubstituteFormValues } from "~Components/forms/substitute.form"
import { CancelableComponent, ModalComponentPropsBase } from "~Shared/context/modal.context"
import { useUpsertSubstitutes } from "~Shared/queries/substitute.queries"
import { NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { isSet } from "~Shared/utils/helper.utils"

interface SubstituteModalProps extends CancelableComponent, ModalComponentPropsBase {
    companyId: string
    existingSubstitutes: Array<Substitute>
}

export const SubstituteModal = ({ isShowing, onCancel, companyId, existingSubstitutes }: SubstituteModalProps) => {
    const { t } = useTranslation()
    const { mutate: upsertSubstitutes, isLoading } = useUpsertSubstitutes({
        companyId,
        onSuccess: () => {
            onCancel?.()
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t("notification:employee_absence.substitute_updated.title"),
                t("notification:employee_absence.substitute_updated.message"),
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:error"))
        },
    })
    const onSubmit = async (values: SubstituteFormValues) => {
        const substitutes = [{ userId: values.primary_substitute, notified: false }]
        if (isSet(values.secondary_substitute)) {
            substitutes.push({ userId: values.secondary_substitute, notified: false })
        }
        upsertSubstitutes(substitutes)
    }
    const [formInstance] = Form.useForm<SubstituteFormValues>()

    useEffect(() => {
        if (existingSubstitutes) {
            formInstance.setFieldsValue({
                primary_substitute: existingSubstitutes[0]?.userId,
                secondary_substitute: existingSubstitutes[1]?.userId,
            })
        }
    }, [existingSubstitutes, formInstance])

    const primarySubstitute = Form.useWatch(["primary_substitute"], formInstance)
    const secondarySubstitute = Form.useWatch(["secondary_substitute"], formInstance)
    const isFormDirty = existingSubstitutes
        ? existingSubstitutes[0]?.userId !== primarySubstitute || existingSubstitutes[1]?.userId !== secondarySubstitute
        : formInstance.isFieldsTouched()

    return (
        <Modal
            open={isShowing}
            title={t("action:employee_absence.add_substitute")}
            onCancel={onCancel}
            onOk={formInstance.submit}
            okText={t("action:save")}
            confirmLoading={isLoading}
            cancelText={t("action:cancel")}
            okButtonProps={{ disabled: !isFormDirty }}
            destroyOnClose
            closable
            keyboard
        >
            <SubstituteForm formInstance={formInstance} onSubmit={onSubmit} />
        </Modal>
    )
}
